import L, { Icon } from 'leaflet';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, useMap } from 'react-leaflet';
import '@/leaflet-ruler.css';
import '@/leaflet-ruler.js';
import { gridSize } from '@/shared';
import { Box, Grid, Paper, Typography } from '@mui/material';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import 'leaflet/dist/leaflet.css';
import { EstadoPuertoNap, Naps } from '@/shared/interfaces';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const url = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const attribution =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

export type CoordenadasType = {
  lat: number;
  lng: number;
};

const activeNapIcon = new Icon({
  iconUrl: 'https://img.icons8.com/?size=256&id=VWPWXStmSlMz&format=png',
  iconSize: [39, 39],
  iconAnchor: [22, 94],
  popupAnchor: [-3, -76],
  className: 'nap__icon--active',
});

export type MapProps = {
  coordenadas: CoordenadasType;
  napsByCity?: Naps[];
  setLatLng?: (coordenadas: CoordenadasType) => void;
  canRuler?: boolean;
  size?: {
    xs: number
    sm: number
    md: number
    lg: number
  }
  canDragMarker?: boolean;
  zoomMap?: number;
};

const CustomMapComponent: React.FC<MapProps> = ({
  coordenadas,
  napsByCity,
  setLatLng,
  size = gridSize,
  canRuler = true,
  canDragMarker = true,
  zoomMap = 16,
}) => {
  const center = [coordenadas.lat, coordenadas.lng];

  ///* draggable
  const [draggable, setDraggable] = useState(false);
  const rulerControlRef = useRef<any>(null);
  const markerRef = useRef(null);
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          setLatLng && setLatLng((marker as any)?.getLatLng());
        }
      },
    }),
    []
  );
  const toggleDraggable = useCallback(() => {
    setDraggable(d => !d);
  }, []);

  ///* recenter
  const RecenterAutomatically = ({ lat, lng }: CoordenadasType) => {
    const map = useMap();
    const animateRef = useRef(true);
    useEffect(() => {
      map.setView([lat, lng], map.getZoom(), {
        animate: animateRef.current || false,
        duration: 1,
      });
    }, [lat, lng]);

    return null;
  };

  ///* ruler
  function LeafletRuler() {
    const map = useMap();

    useEffect(() => {
      if (!map) return;

      if (!rulerControlRef.current) {
        rulerControlRef.current = (L.control as any).ruler().addTo(map);
      }

      return () => {
        if (rulerControlRef.current) {
          map.removeControl(rulerControlRef.current);
          rulerControlRef.current = null;
        }
      };
    }, [map]);

    return null;
  }

  return (
    <Grid
      item
      {...size}
      sx={{
        pb: 4,
      }}
    >
      <Paper elevation={3} style={{ height: '600px', width: '100%' }}>
        <MapContainer
          center={center as L.LatLngExpression}
          zoom={zoomMap}
          scrollWheelZoom={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer url={url} attribution={attribution} />

          {center && (
            <Marker
              position={center as L.LatLngExpression}
              draggable={canDragMarker ?? draggable}
              ref={markerRef}
              eventHandlers={eventHandlers}
            >
              <Popup minWidth={90}>
                {canDragMarker ? (
                  <Box
                    component="span"
                    onClick={toggleDraggable}
                    style={{
                      cursor: 'pointer',
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    {draggable
                      ? 'Ya puede arrastrar y soltar el Marker'
                      : 'Click aquí para activar el Marker'}
                  </Box>
                ) : (
                  'Ubicación Cliente'
                )}
              </Popup>
            </Marker>
          )}

          {/* naps */}
          {napsByCity?.length
            ? napsByCity.map(nap => {
              const [lat, lng] = nap?.coordenadas?.split(',') ?? [];
              const isValidLatOrLng =
                !isNaN(Number(lat)) &&
                !isNaN(Number(lng)) &&
                +lat !== 0 &&
                +lng !== 0;

              return (
                <>
                  {isValidLatOrLng && (
                    <Marker
                      key={nap.id_nap}
                      position={[+lat, +lng] as L.LatLngExpression}
                      title={nap.nombre}
                      icon={activeNapIcon}
                    >
                      <Popup>
                        <div>
                          <Typography variant="h6">{nap.nombre}</Typography>
                          <Box display="flex" flexDirection="column">
                            <Box maxWidth={"100%"}>
                              <Typography variant="body2">
                                {`Puertos disponibles: `}
                              </Typography>
                              <Typography maxWidth={"100%"} variant="subtitle1" sx={{ overflowWrap: "break-word"}}>
                                {nap.puertos?.some(e => 
                                  e.estado === EstadoPuertoNap.DISPONIBLE || e.estado === EstadoPuertoNap.LIBRE
                                ) ? (
                                  nap.puertos?.filter(e => 
                                    e.estado === EstadoPuertoNap.DISPONIBLE || e.estado === EstadoPuertoNap.LIBRE
                                  )?.map(nap => `P${nap.puerto}`).join(', ')
                                ) : ('Sin puertos disponibles')}
                              </Typography>
                            </Box>

                            <Box>
                              <Typography variant="body2">
                                Coordenadas:{' '}
                              </Typography>
                              <strong>
                                {lat}, {lng}
                              </strong>
                            </Box>
                          </Box>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </>
              );
            })
            : null}

          <RecenterAutomatically lat={center[0]} lng={center[1]} />
          {canRuler && <LeafletRuler />}
        </MapContainer>
      </Paper>
    </Grid>
  );
};

export default CustomMapComponent;